import { useCallback, useEffect, useRef, useState } from "react";
import { useDeviceSelectors } from "react-device-detect";
import { useMobileTouchHandler } from "components/DateTimePicker/hooks/useMobileTouchHandler";

interface UseInputValue<T = any> {
  value?: T;
  defaultValue?: T;
  onChange?(v?: T): void;
  isReadOnly?: boolean;
}

export const useInputValue = ({
  value,
  defaultValue = "",
  onChange,
  isReadOnly,
}: UseInputValue) => {
  const [state, setState] = useState(value ?? defaultValue);
  const stateRef = useRef();
  stateRef.current = state;
  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);
  const [{ isMobile }] = useDeviceSelectors(window.navigator.userAgent);

  useEffect(() => {
    setState(value ?? defaultValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    if (isReadOnly) return;
    onChange?.(stateRef.current);
  }, [onChange, isReadOnly]);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  // Blur input when touching forge viewer on mobile (only if focused)
  useMobileTouchHandler(
    isMobile,
    useCallback(() => {
      if (isFocusedRef.current && inputRef.current) {
        inputRef.current.blur();
      }
    }, [])
  );

  useEffect(() => {
    return () => {
      if(isFocusedRef.current) {
        handleBlur();
      }
    }
  }, [handleBlur])

  return {
    value: state,
    ref: inputRef,
    onChange: useCallback(
      (value: any) => {
        if (isReadOnly) return;
        setState(value);
      },
      [isReadOnly]
    ),
    onBlur: handleBlur,
    onFocus: handleFocus,
  };
};
